import { getAnalyticsPreferences } from "~/utils/analytics-preferences.client";

export type AccountEngagementEvent =
  | "profile_session_started"
  | "profile_session_ended"
  | "booking_created";

interface AccountContext {
  accountId: string;
  accountType?: "doctor" | "organization";
  bookingId?: string;
  doctorSlug?: string;
}

const SESSION_KEY = "laruche_public_profile_session";
const VISITOR_KEY = "laruche_public_profile_visitor";
const ATTRIBUTION_KEY_PREFIX = "laruche_share_attribution:";

export interface AcquisitionTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  referrer_domain?: string;
  landing_path?: string;
}

interface AccountAttribution {
  shareId?: string;
  token?: string;
  channel: string;
  doctorSlug?: string;
  firstTouch?: AcquisitionTouch;
  lastTouch?: AcquisitionTouch;
  sessionId?: string;
  visitorId?: string;
}

interface StoredAttribution {
  shareId?: string;
  token?: string;
  channel: string;
  doctorSlug: string;
  firstTouch: AcquisitionTouch;
  lastTouch: AcquisitionTouch;
}

const createId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const persistedId = (key: string, storage: Storage) => {
  const existing = storage.getItem(key);
  if (existing) return existing;
  const value = createId();
  storage.setItem(key, value);
  return value;
};

const doctorSlugFromLocation = () => {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const marker = segments.findIndex(value => value === "doctor" || value === "doctors");
  return marker >= 0 ? segments[marker + 1] : undefined;
};

const referrerDomain = () => {
  try {
    return document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : undefined;
  } catch {
    return undefined;
  }
};

const touchFromLocation = (params: URLSearchParams): AcquisitionTouch => ({
  source: params.get("utm_source") || undefined,
  medium: params.get("utm_medium") || undefined,
  campaign: params.get("utm_campaign") || undefined,
  content: params.get("utm_content") || undefined,
  term: params.get("utm_term") || undefined,
  referrer_domain: referrerDomain(),
  landing_path: window.location.pathname,
});

const hasAcquisitionSignal = (touch: AcquisitionTouch) =>
  Boolean(touch.source || touch.medium || touch.campaign || touch.referrer_domain);

const stripAttributionQuery = (params: URLSearchParams) => {
  [
    "sid", "share", "ref", "channel", "utm_source", "utm_medium",
    "utm_campaign", "utm_content", "utm_term",
  ].forEach(key => params.delete(key));
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
  );
};

export const getAccountAttribution = (doctorSlug?: string): AccountAttribution => {
  try {
    const params = new URLSearchParams(window.location.search);
    const shareId = params.get("sid") || undefined;
    const token = params.get("share") || params.get("ref");
    const channel = params.get("channel") || "unknown";
    const resolvedDoctorSlug = doctorSlug || doctorSlugFromLocation();
    const sessionId = persistedId(SESSION_KEY, window.sessionStorage);
    const visitorId = persistedId(VISITOR_KEY, window.localStorage);
    const currentTouch = touchFromLocation(params);
    if (!resolvedDoctorSlug) {
      return {
        shareId,
        token: token || undefined,
        channel,
        firstTouch: currentTouch,
        lastTouch: currentTouch,
        sessionId,
        visitorId,
      };
    }
    const storageKey = `${ATTRIBUTION_KEY_PREFIX}${resolvedDoctorSlug}`;
    const storedRaw = window.sessionStorage.getItem(storageKey);
    const stored = storedRaw ? (JSON.parse(storedRaw) as StoredAttribution) : undefined;
    if (shareId || token) {
      const value: StoredAttribution = {
        shareId,
        token: token || undefined,
        channel,
        doctorSlug: resolvedDoctorSlug,
        firstTouch: stored?.firstTouch || currentTouch,
        lastTouch: currentTouch,
      };
      window.sessionStorage.setItem(storageKey, JSON.stringify(value));
      stripAttributionQuery(params);
      return { ...value, sessionId, visitorId };
    }

    const firstTouch = stored?.firstTouch || currentTouch;
    const lastTouch = hasAcquisitionSignal(currentTouch)
      ? currentTouch
      : stored?.lastTouch || currentTouch;
    const value: StoredAttribution = {
      shareId: stored?.shareId,
      token: stored?.token,
      channel: stored?.channel || "unknown",
      doctorSlug: resolvedDoctorSlug,
      firstTouch,
      lastTouch,
    };
    window.sessionStorage.setItem(storageKey, JSON.stringify(value));
    return {
      ...value,
      sessionId,
      visitorId,
    };
  } catch {
    return {
      token: undefined,
      shareId: undefined,
      channel: "unknown",
      sessionId: undefined,
      visitorId: undefined,
    };
  }
};

const eventContext = (doctorSlug?: string) => {
  const attribution = getAccountAttribution(doctorSlug);
  return {
    session_id: attribution.sessionId,
    visitor_id: attribution.visitorId,
    attribution_token: attribution.token,
    attribution_share_id: attribution.shareId,
    channel: attribution.channel,
    attribution_first_touch: attribution.firstTouch,
    attribution_last_touch: attribution.lastTouch,
  };
};

export const appendAccountAttribution = (formData: FormData, doctorSlug?: string) => {
  if (typeof window === "undefined") return;
  const attribution = getAccountAttribution(doctorSlug);
  const preferences = getAnalyticsPreferences();
  formData.set("analytics_consent", String(preferences.analytics));
  formData.set("marketing_consent", String(preferences.marketing));
  if (attribution.token) {
    formData.set("attribution_token", attribution.token);
  }
  if (attribution.shareId) {
    formData.set("attribution_share_id", attribution.shareId);
  }
  formData.set("attribution_channel", attribution.channel);
  if (attribution.sessionId) {
    formData.set("attribution_session_id", attribution.sessionId);
  }
  if (attribution.visitorId) {
    formData.set("attribution_visitor_id", attribution.visitorId);
  }
  if (attribution.firstTouch) {
    formData.set("analytics_first_touch", JSON.stringify(attribution.firstTouch));
  }
  if (attribution.lastTouch) {
    formData.set("analytics_last_touch", JSON.stringify(attribution.lastTouch));
  }
};

export const trackAccountEngagement = (
  eventType: AccountEngagementEvent,
  context: AccountContext,
  extras: {
    eventId?: string;
    activeDurationSeconds?: number;
    preferBeacon?: boolean;
  } = {}
) => {
  if (typeof window === "undefined" || !context.accountId) return;
  const common = eventContext(context.doctorSlug);
  const body = JSON.stringify({
    event_id: extras.eventId || createId(),
    event_type: eventType,
    account_type: context.accountType || "doctor",
    account_id: context.accountId,
    booking_id: context.bookingId,
    active_duration_seconds: extras.activeDurationSeconds,
    ...common,
  });

  if (
    extras.preferBeacon &&
    typeof navigator.sendBeacon === "function" &&
    navigator.sendBeacon(
      "/api/account-engagement-event",
      new Blob([body], { type: "application/json" })
    )
  ) {
    return;
  }

  void fetch("/api/account-engagement-event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body,
  }).catch(() => {
    // Analytics never blocks browsing or booking.
  });
};

export const startPublicProfileSession = (context: AccountContext) => {
  if (typeof window === "undefined" || !context.accountId) return () => {};
  const profileVisitId = createId();
  const startedAt = Date.now();
  let activeStartedAt = document.visibilityState === "visible" ? Date.now() : 0;
  let activeMilliseconds = 0;
  let ended = false;

  trackAccountEngagement("profile_session_started", context, {
    eventId: `profile-start:${profileVisitId}`,
  });

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      activeStartedAt = Date.now();
    } else if (activeStartedAt) {
      activeMilliseconds += Date.now() - activeStartedAt;
      activeStartedAt = 0;
    }
  };

  const end = () => {
    if (ended) return;
    ended = true;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", end);
    if (activeStartedAt) activeMilliseconds += Date.now() - activeStartedAt;
    const duration = Math.max(
      1,
      Math.round(
        Math.min(activeMilliseconds, Date.now() - startedAt) / 1000
      )
    );
    trackAccountEngagement("profile_session_ended", context, {
      eventId: `profile-end:${profileVisitId}`,
      activeDurationSeconds: duration,
      preferBeacon: true,
    });
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", end, { once: true });
  return end;
};
